'use client';

import {
  useEffect,
  useRef,
  useState
} from 'react';

// Reveals an element once it scrolls into view, then stops observing it -
// a repeat visitor scrolling back up shouldn't see it fade out and back in.
//
// rootMargin defaults to a generous 600px lookahead - anything gated on
// isVisible (like a Lottie fetch) starts loading well before the element
// is actually on screen, so it's ready by the time the user scrolls to it
// instead of visibly popping in a beat late. A tight/zero margin here was
// the direct cause of a real "things move around" bug: the fetch only
// started once the element was already visible.
export function useScrollReveal<T extends HTMLElement = HTMLElement>(
  threshold = 0.15,
  rootMargin = '600px 0px'
) {

  const ref = useRef<T>(null);
  const [ isVisible, setIsVisible ] = useState(false);

  useEffect(() => {

    const el = ref.current;

    if (!el) {

      return;
    }

    const observer = new IntersectionObserver(([ entry ]) => {

      if (entry?.isIntersecting) {

        setIsVisible(true);
        observer.unobserve(el);
      }
    }, {
      threshold,
      rootMargin
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, [ threshold, rootMargin ]);

  return {
    ref,
    isVisible
  };
}
